import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, reportClientError } from "@/lib/queryClient";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  commercialStepSchema,
  type CommercialStepData,
  type ContactStepData,
  type RequestDetailsStepData,
  type AIScopingResponse,
} from "@/lib/intake-schemas";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { AIScopePreview } from "./ai-scope-preview";
import {
  Loader2,
  AlertCircle,
  BookOpen,
  CreditCard,
  ShieldCheck,
  FileText,
  CalendarClock,
  Zap,
  CheckCircle2,
  Clock,
  CircleDollarSign,
  ArrowLeft,
  Send,
  ExternalLink,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { toAIScopingInternal, isScopingUnavailable } from "@shared/intake-ai-scoping";
import { intakeStackedPrimaryCtaClassName } from "@/lib/intake-cta";
import { SubmissionGuard } from "@/lib/submission-guard";
import {
  canOfferIntakePaymentHold,
  isIntakePaymentHoldRequired,
  isIntakeRequestCovered,
  resolveCardHoldAction,
  resolvePricingGate,
  type IntakePaymentHoldPolicy,
} from "@shared/contracts/intake";

// ─── Helpers ────────────────────────────────────────────────

interface PricingInfo {
  currency: string;
  smallTaskFee: number;
  mediumTaskFee: number;
  hourlyRate: number;
  schedulingLinkUrl: string | null;
  intakePaymentHoldPolicy: IntakePaymentHoldPolicy;
}

type ProductiveCoverageInfo = {
  configured: boolean;
  coverage: "covered" | "no_active_service" | "unmatched";
  reject: boolean;
  message: string;
};

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

// Initialize Stripe promise (singleton)
let stripePromise: Promise<Stripe | null>;

const getStripe = async () => {
  if (!stripePromise) {
    const res = await apiRequest("GET", "/api/billing/stripe/config");
    const { publicKey } = await res.json();
    stripePromise = loadStripe(publicKey);
  }
  return stripePromise;
};

interface CommercialStepProps {
  contactData: ContactStepData;
  detailsData: RequestDetailsStepData;
  aiScoping: AIScopingResponse;
  onSubmit: () => void;
  onBack: () => void;
}

// Small reusable component for the "What happens next" timeline
function TimelineStep({
  step,
  icon,
  title,
  description,
  accent,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={`flex items-center justify-center w-9 h-9 rounded-lg ${accent} shrink-0`}
      >
        {icon}
      </div>
      <div>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
          Step {step}
        </p>
        <p className="text-sm font-semibold leading-tight">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  );
}

function CardPaymentFields({
  binding,
  onChange,
  onReadyChange,
}: {
  binding: MutableRefObject<{ stripe: Stripe | null; elements: any }>;
  onChange: (state: { empty: boolean; complete: boolean }) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    binding.current = { stripe, elements };
    onReadyChange(Boolean(stripe && elements));
    return () => {
      binding.current = { stripe: null, elements: null };
      onReadyChange(false);
    };
  }, [binding, elements, onReadyChange, stripe]);

  return (
    <PaymentElement
      onChange={(event) => {
        onChange({ empty: event.empty, complete: event.complete });
      }}
    />
  );
}

// Inner form. Stripe mounts inside the card section so opting into a hold
// does not remount the rest of the review step.
function CommercialForm({
  contactData,
  detailsData,
  aiScoping,
  onSubmit,
  onBack,
  clientSecret,
  paymentIntentId,
  paymentRequired,
  paymentHoldPolicy,
  paymentHoldAvailable,
  paymentHoldEnabled,
  holdRequested,
  onHoldRequestedChange,
  isUrgent,
  onUrgentChange,
  pricing,
  paymentAmount,
  paymentSetupPending = false,
  stripeInstance,
  productiveCoverage,
  isCovered,
}: CommercialStepProps & {
  clientSecret?: string;
  paymentIntentId?: string;
  paymentRequired: boolean;
  paymentHoldPolicy: IntakePaymentHoldPolicy;
  paymentHoldAvailable: boolean;
  paymentHoldEnabled: boolean;
  holdRequested: boolean;
  onHoldRequestedChange: (value: boolean) => void;
  isUrgent: boolean;
  onUrgentChange: (value: boolean) => void;
  pricing: PricingInfo | null;
  paymentAmount?: number;
  paymentSetupPending?: boolean;
  stripeInstance: Stripe | null;
  productiveCoverage?: ProductiveCoverageInfo | null;
  isCovered: boolean;
}) {
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [cardFields, setCardFields] = useState({
    empty: true,
    complete: false,
  });
  const [cardElementReady, setCardElementReady] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const submissionGuard = useRef(new SubmissionGuard());
  const confirmedPaymentIntentRef = useRef<string | undefined>(undefined);
  const stripeBinding = useRef<{ stripe: Stripe | null; elements: any }>({
    stripe: null,
    elements: null,
  });

  useEffect(() => {
    if (!paymentHoldEnabled) {
      setCardFields({ empty: true, complete: false });
      setCardElementReady(false);
      stripeBinding.current = { stripe: null, elements: null };
    }
  }, [paymentHoldEnabled]);

  const form = useForm<CommercialStepData>({
    resolver: zodResolver(commercialStepSchema),
    defaultValues: {
      agreedToTerms: false,
      isUrgent: false,
      userProvidedMissingInfo: "",
      userAssumptionContext: "",
    },
  });

  const releaseSubmission = () => {
    submissionGuard.current.release();
    setIsSubmittingRequest(false);
  };

  const submitMutation = useMutation({
    mutationFn: async ({
      data,
      confirmedPaymentIntentId,
    }: {
      data: CommercialStepData;
      confirmedPaymentIntentId?: string;
    }) => {
      const response = await apiRequest("POST", "/api/intake/submit", {
        contactData,
        detailsData,
        aiScoping: toAIScopingInternal(aiScoping),
        commercialData: data,
        paymentIntentId: confirmedPaymentIntentId,
        estimatedSize: aiScoping.estimate_bucket,
      });
      return response;
    },
    onSuccess: () => {
      onSubmit();
    },
    onError: () => {
      releaseSubmission();
    },
  });

  const submissionBusy = isSubmittingRequest || submitMutation.isPending;

  const handleFormSubmit = async (data: CommercialStepData) => {
    if (!submissionGuard.current.tryBegin()) return;

    setIsSubmittingRequest(true);
    setPaymentError(null);
    let keepLocked = false;

    try {
      if (paymentRequired && !paymentHoldEnabled) {
        setPaymentError(
          "A card hold is required before you can submit this request.",
        );
        return;
      }

      if (confirmedPaymentIntentRef.current) {
        keepLocked = true;
        submitMutation.mutate({
          data,
          confirmedPaymentIntentId: confirmedPaymentIntentRef.current,
        });
        return;
      }

      if (paymentHoldEnabled) {
        if (paymentSetupPending) return;

        if (!clientSecret || !stripeInstance) {
          setPaymentError(
            paymentRequired
              ? "We couldn't start the card hold. Try again in a moment."
              : "We couldn't start the card hold. Clear the card option to submit without one.",
          );
          return;
        }

        const { stripe: stripeClient, elements } = stripeBinding.current;
        if (!stripeClient || !elements || !cardElementReady) return;

        const action = resolveCardHoldAction({
          paymentRequired,
          empty: cardFields.empty,
          complete: cardFields.complete,
        });

        if (action === "skip") {
          keepLocked = true;
          submitMutation.mutate({ data });
          return;
        }

        if (action === "error" && !paymentRequired) {
          setPaymentError(
            "Enter the full card details, or clear them to submit without a card hold.",
          );
          return;
        }

        const { error } = await stripeClient.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

        if (error) {
          reportClientError(
            new Error(`Stripe confirmPayment failed: ${error.message}`),
            { feature: "intake", step: "payment" },
          );
          setPaymentError(error.message ?? "Payment failed");
          return;
        }

        confirmedPaymentIntentRef.current = paymentIntentId;
        keepLocked = true;
        submitMutation.mutate({
          data,
          confirmedPaymentIntentId: paymentIntentId,
        });
        return;
      }

      keepLocked = true;
      submitMutation.mutate({ data });
    } finally {
      if (!keepLocked) releaseSubmission();
    }
  };

  const agreedToTerms = form.watch("agreedToTerms");
  const canToggleHold =
    paymentHoldAvailable &&
    !(paymentRequired && paymentHoldPolicy === "required") &&
    !(paymentRequired && paymentHoldPolicy === "optional_if_urgent" && isUrgent);

  useEffect(() => {
    form.setValue("isUrgent", isUrgent);
  }, [form, isUrgent]);

  const scopingUnavailable = isScopingUnavailable(aiScoping);
  const bucket = aiScoping.estimate_bucket as string | undefined;
  const isLarge = !scopingUnavailable && bucket === "large";
  const cur = pricing?.currency || "GBP";

  // Determine the displayed amount
  const displayAmount =
    paymentAmount ??
    (bucket === "small" ? pricing?.smallTaskFee : undefined) ??
    (bucket === "medium" ? pricing?.mediumTaskFee : undefined);

  return (
    <div className="py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-8">
        <p className="text-sm font-medium text-primary mb-2 tracking-wide uppercase">
          Final check
        </p>
        <h1 className="text-3xl font-bold mb-2 font-heading">
          Review & Confirm
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Check the summary below, then submit your request.
        </p>
      </div>

      {scopingUnavailable && (
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            We couldn't automatically size this request. Submit anyway — our
            team will review it and follow up.
          </AlertDescription>
        </Alert>
      )}

      {/* AI Scope Preview */}
      {!scopingUnavailable && (
        <AIScopePreview aiScoping={aiScoping} isCovered={isCovered} />
      )}

      {/* Self-Service Help Docs Section */}
      {!scopingUnavailable &&
        aiScoping.help_docs &&
        aiScoping.help_docs.length > 0 && (
        <Card className="mt-6 border-primary/20 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-primary/60 to-primary/10" />
          <CardContent className="pt-6 pb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold font-heading">
                  Helpful self-service resources
                </h3>
                <p className="text-xs text-muted-foreground">
                  If one of these solves the issue, you may not need to raise
                  the request at all.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {aiScoping.help_docs.map((doc, i) => (
                <a
                  key={i}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-accent/30 rounded-lg border border-transparent hover:border-primary/30 transition-all group"
                >
                  <span className="font-medium text-sm group-hover:text-primary transition-colors flex items-center gap-2">
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary" />
                    {doc.title}
                  </span>
                  {doc.relevance && (
                    <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full text-muted-foreground font-medium">
                      {doc.relevance} match
                    </span>
                  )}
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="space-y-6 mt-8"
        >
          <div className="space-y-6">
            {/* ─── Payment / Coverage Status ─── */}

            {isCovered ? (
              <Card className="border-emerald-200 dark:border-emerald-800 overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-emerald-500/60 to-emerald-500/10" />
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-emerald-800 dark:text-emerald-300 font-heading">
                        Covered by Your Maintenance Plan
                      </h3>
                      <p className="text-sm text-emerald-700 dark:text-emerald-400">
                        No payment required. If additional work is needed beyond
                        the plan scope, we'll let you know first.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : paymentHoldAvailable && displayAmount ? (
              <Card className="border-blue-200 dark:border-blue-800 overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-blue-500/60 to-blue-500/10" />
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                        <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-blue-900 dark:text-blue-200 font-heading">
                          {paymentRequired
                            ? "Card Hold Required"
                            : "Optional Card Hold"}
                        </h3>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {bucket === "small"
                            ? "This request fits our quick-fix fee."
                            : "This request fits our medium task fee."}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                        {formatCurrency(displayAmount, cur)}
                      </span>
                      <span className="block text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                        + VAT where applicable
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg bg-blue-100/50 dark:bg-blue-900/30 border border-blue-200/50 dark:border-blue-700/50 p-3 mb-5">
                    <p className="text-xs text-blue-800 dark:text-blue-300 flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        <strong>Authorisation hold only.</strong> You will{" "}
                        <strong>not</strong> be charged when you submit this
                        request. If the work cannot go ahead, the hold is
                        released automatically.
                      </span>
                    </p>
                  </div>

                  {!paymentRequired && (
                    <div className="mb-5 rounded-lg border border-blue-200/60 bg-white/70 p-4 dark:border-blue-700/50 dark:bg-background">
                      <label className="flex items-start gap-3">
                        <Checkbox
                          checked={holdRequested}
                          onCheckedChange={(checked) => {
                            if (submissionBusy) return;
                            onHoldRequestedChange(checked === true);
                          }}
                          disabled={!canToggleHold || submissionBusy}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            {paymentHoldPolicy === "optional_if_urgent" && isUrgent
                              ? "Urgent requests need a card hold"
                              : "Add a card hold before submitting"}
                          </p>
                          <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                            {paymentHoldPolicy === "optional_if_urgent" && isUrgent
                              ? "Because this request is marked urgent, a card hold is required before submission."
                              : paymentHoldPolicy === "optional_if_urgent"
                                ? "Optional by default. If you mark the request as urgent, a card hold becomes required."
                                : "Optional. You can still submit without adding a card hold."}
                          </p>
                        </div>
                      </label>
                    </div>
                  )}

                  {paymentSetupPending && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Preparing secure checkout...
                    </p>
                  )}

                  {paymentHoldEnabled && clientSecret && stripeInstance && (
                    <div className="bg-white dark:bg-background p-4 rounded-lg border">
                      <Elements
                        stripe={stripeInstance}
                        options={{
                          clientSecret,
                          appearance: { theme: "stripe" },
                        }}
                      >
                        <CardPaymentFields
                          binding={stripeBinding}
                          onChange={setCardFields}
                          onReadyChange={setCardElementReady}
                        />
                      </Elements>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : isLarge ? (
              <Card className="border-amber-200 dark:border-amber-800 overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-amber-500/60 to-amber-500/10" />
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-amber-900 dark:text-amber-200 font-heading">
                        Custom Quote Required
                      </h3>
                      <p className="text-sm text-amber-800 dark:text-amber-300">
                        This request is estimated as a larger job ({">"}1-2
                        hours). No payment now — submit your request and our
                        team will prepare a detailed quote
                        {pricing?.hourlyRate
                          ? ` based on our rate of ${formatCurrency(pricing.hourlyRate, cur)}/hr + VAT`
                          : ""}
                        .
                      </p>
                    </div>
                  </div>
                  {pricing?.schedulingLinkUrl && (
                    <div className="mt-4 pt-4 border-t border-amber-200/50 dark:border-amber-700/50 ml-12">
                      <p className="text-sm text-amber-800 dark:text-amber-300 mb-3">
                        Want to discuss the project scope in more detail?
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-amber-300 text-amber-900 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-200 dark:hover:bg-amber-900/40 gap-1.5"
                        onClick={() =>
                          window.open(pricing.schedulingLinkUrl!, "_blank")
                        }
                      >
                        <CalendarClock className="w-4 h-4" />
                        Schedule a Call
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : null}

            {paymentError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{paymentError}</AlertDescription>
              </Alert>
            )}

            {productiveCoverage?.coverage === "unmatched" &&
              productiveCoverage.configured && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{productiveCoverage.message}</AlertDescription>
                </Alert>
              )}

            {productiveCoverage?.coverage === "no_active_service" &&
              productiveCoverage.configured && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{productiveCoverage.message}</AlertDescription>
                </Alert>
              )}

            {productiveCoverage?.coverage === "covered" && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{productiveCoverage.message}</AlertDescription>
              </Alert>
            )}

            {/* ─── What Happens Next ─── */}
            <Card className="border-border overflow-hidden">
              <CardContent className="pt-6">
                <h3 className="text-xs font-semibold mb-4 uppercase tracking-wide text-muted-foreground">
                  After you submit
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {isCovered ? (
                    <>
                      <TimelineStep
                        step={1}
                        icon={<CheckCircle2 className="w-4 h-4" />}
                        title="You submit the request"
                        description="We'll send confirmation that it has been received."
                        accent="bg-primary/10 text-primary"
                      />
                      <TimelineStep
                        step={2}
                        icon={<Clock className="w-4 h-4" />}
                        title="We review the details"
                        description="We'll confirm the scope and contact you if anything else is needed."
                        accent="bg-blue-500/10 text-blue-600 dark:text-blue-400"
                      />
                      <TimelineStep
                        step={3}
                        icon={<ShieldCheck className="w-4 h-4" />}
                        title="We complete the work"
                        description="We'll let you know as soon as the request is finished."
                        accent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      />
                    </>
                  ) : isLarge ? (
                    <>
                      <TimelineStep
                        step={1}
                        icon={<CheckCircle2 className="w-4 h-4" />}
                        title="You submit the request"
                        description="We'll review the brief and confirm any missing details."
                        accent="bg-primary/10 text-primary"
                      />
                      <TimelineStep
                        step={2}
                        icon={<FileText className="w-4 h-4" />}
                        title="We send a quote"
                        description="You'll receive a clear quote or proposal for approval."
                        accent="bg-amber-500/10 text-amber-600 dark:text-amber-400"
                      />
                      <TimelineStep
                        step={3}
                        icon={<CircleDollarSign className="w-4 h-4" />}
                        title="You approve"
                        description="Work starts once you're happy to go ahead."
                        accent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      />
                    </>
                  ) : paymentHoldEnabled ? (
                    <>
                      <TimelineStep
                        step={1}
                        icon={<CreditCard className="w-4 h-4" />}
                        title="Card hold placed"
                        description="This is only an authorisation hold, not a charge."
                        accent="bg-blue-500/10 text-blue-600 dark:text-blue-400"
                      />
                      <TimelineStep
                        step={2}
                        icon={<Clock className="w-4 h-4" />}
                        title="We complete the work"
                        description="We'll review the request and carry out the task."
                        accent="bg-primary/10 text-primary"
                      />
                      <TimelineStep
                        step={3}
                        icon={<CheckCircle2 className="w-4 h-4" />}
                        title="Charge captured"
                        description="You're only charged once the work is finished."
                        accent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      />
                    </>
                  ) : (
                    <>
                      <TimelineStep
                        step={1}
                        icon={<CheckCircle2 className="w-4 h-4" />}
                        title="You submit the request"
                        description="We'll confirm that it has been received."
                        accent="bg-primary/10 text-primary"
                      />
                      <TimelineStep
                        step={2}
                        icon={<Clock className="w-4 h-4" />}
                        title="We review the details"
                        description="We'll check the scope and contact you if anything else is needed."
                        accent="bg-blue-500/10 text-blue-600 dark:text-blue-400"
                      />
                      <TimelineStep
                        step={3}
                        icon={<CheckCircle2 className="w-4 h-4" />}
                        title="We confirm next steps"
                        description="We'll let you know once the work is complete or if any billing approval is needed."
                        accent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      />
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* ─── Urgent Flag ─── */}
            <FormField
              control={form.control}
              name="isUrgent"
              render={({ field }) => (
                <Card className="border-amber-200/50 dark:border-amber-800/30 overflow-hidden">
                  <div className="h-0.5 bg-gradient-to-r from-amber-400/40 to-transparent" />
                  <CardContent className="pt-4 pb-4">
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          disabled={submissionBusy}
                          onCheckedChange={(checked) => {
                            if (submissionBusy) return;
                            const nextValue = checked === true;
                            field.onChange(nextValue);
                            onUrgentChange(nextValue);
                          }}
                          className="mt-1"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none flex-1">
                        <FormLabel className="text-sm font-medium cursor-pointer flex items-center gap-2">
                          <Zap className="w-4 h-4 text-amber-600" />
                          This is urgent — prioritise my request
                        </FormLabel>
                        <FormDescription className="text-xs">
                          Standard turnaround is 2–3 working days. Check this to
                          flag as priority — our team will aim to start within 24
                          hours.
                        </FormDescription>
                      </div>
                    </FormItem>
                  </CardContent>
                </Card>
              )}
            />

            {/* ─── Terms Agreement ─── */}
            <FormField
              control={form.control}
              name="agreedToTerms"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border border-border p-4 bg-background">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-1"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none flex-1">
                    <FormLabel className="text-sm font-medium cursor-pointer">
                      {isCovered
                        ? "I confirm the details above are correct and wish to proceed *"
                        : "I agree to the following terms *"}
                    </FormLabel>
                    <FormDescription className="text-sm pt-2">
                      {isCovered ? (
                        <span className="block text-xs">
                          Your request will be reviewed by our team. If
                          additional work beyond your plan scope is required,
                          we'll contact you before proceeding.
                        </span>
                      ) : paymentRequired && displayAmount ? (
                        <span className="block text-xs">
                          I authorise a hold of{" "}
                          <strong>
                            {formatCurrency(displayAmount, cur)}
                          </strong>{" "}
                          on my card. I understand I will only be charged once
                          the work is completed. If the request cannot be
                          fulfilled, the hold will be released.
                        </span>
                      ) : isLarge && pricing?.hourlyRate ? (
                        <span className="block text-xs">
                          I understand this request will be quoted separately at{" "}
                          <strong>
                            {formatCurrency(pricing.hourlyRate, cur)}/hr + VAT
                          </strong>
                          . No payment is taken now — work begins only after I
                          approve the quote.
                        </span>
                      ) : (
                        <span className="block text-xs">
                          By submitting, you authorise us to proceed. Usage is
                          billed at the applicable rate + VAT.
                        </span>
                      )}
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onBack}
              disabled={submissionBusy}
              className="order-2 sm:order-1 gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              type="submit"
              size="lg"
              disabled={
                submissionBusy ||
                !agreedToTerms ||
                paymentSetupPending ||
                (paymentHoldEnabled && Boolean(clientSecret) && !cardElementReady)
              }
              className={`${intakeStackedPrimaryCtaClassName} order-1 sm:order-2 sm:flex-initial sm:min-w-[200px] active-elevate-2 gap-1.5`}
            >
              {submissionBusy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export function CommercialStep(props: CommercialStepProps) {
  const { aiScoping, contactData, detailsData } = props;
  const [isUrgent, setIsUrgent] = useState(false);
  const [holdRequested, setHoldRequested] = useState(false);
  const userOptedInRef = useRef(false);
  const bucket = isScopingUnavailable(aiScoping)
    ? undefined
    : (aiScoping.estimate_bucket as "small" | "medium" | "large" | undefined);

  const {
    data: productiveCoverage,
    isPending: coveragePending,
    isFetching: coverageFetching,
    refetch: refetchCoverage,
  } = useQuery<ProductiveCoverageInfo>({
    queryKey: [
      "/api/intake/coverage",
      contactData.email,
      contactData.company,
      contactData.phone || "",
      detailsData.category,
      JSON.stringify(detailsData.requestDetails ?? {}),
    ],
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/intake/coverage", {
        email: contactData.email,
        company: contactData.company,
        phone: contactData.phone || undefined,
        category: detailsData.category,
        requestDetails: detailsData.requestDetails,
      });
      return res.json();
    },
    staleTime: 30_000,
  });

  const coverageGate = productiveCoverage
    ? { status: "ready" as const }
    : coveragePending
      ? { status: "loading" as const }
      : { status: "error" as const };

  const isCovered = isIntakeRequestCovered({
    productiveCoverage: productiveCoverage?.coverage,
    requiresPayment: aiScoping.requiresPayment,
  });

  // Fetch public pricing (always — needed for all flows).
  // Queries do not retry (see the shared query client), so a failure stays
  // failed until the user asks to load it again.
  const {
    data: pricing,
    isPending: pricingPending,
    isFetching: pricingFetching,
    refetch: refetchPricing,
  } = useQuery<PricingInfo>({
    queryKey: ["/api/intake/pricing"],
    queryFn: async () => {
      const res = await fetch("/api/intake/pricing", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch pricing");
      return res.json();
    },
    staleTime: 60_000,
  });

  // Don't resolve the policy until pricing has loaded. The shared resolver
  // treats a missing policy as "required", which latches the optional
  // checkbox on during the request. A failed load is an error with a retry,
  // not a disabled hold.
  const pricingGate = resolvePricingGate({
    pricing,
    isPending: pricingPending,
  });
  const paymentHoldPolicy: IntakePaymentHoldPolicy | undefined =
    pricingGate.status === "ready" ? pricingGate.policy : undefined;
  const paymentHoldAvailable = paymentHoldPolicy
    ? canOfferIntakePaymentHold({
        estimateBucket: bucket,
        isCovered,
        policy: paymentHoldPolicy,
      })
    : false;
  const paymentRequired = paymentHoldPolicy
    ? isIntakePaymentHoldRequired({
        estimateBucket: bucket,
        isCovered,
        policy: paymentHoldPolicy,
        isUrgent,
      })
    : false;
  const paymentHoldEnabled =
    paymentHoldAvailable && (paymentRequired || holdRequested);
  const holdIsForced =
    paymentHoldAvailable &&
    (paymentHoldPolicy === "required" ||
      (paymentHoldPolicy === "optional_if_urgent" && isUrgent));

  const handleHoldRequestedChange = (value: boolean) => {
    if (!holdIsForced) {
      userOptedInRef.current = value;
    }
    setHoldRequested(value);
  };

  useEffect(() => {
    if (!paymentHoldPolicy) return;

    if (!paymentHoldAvailable) {
      userOptedInRef.current = false;
      setHoldRequested(false);
      return;
    }

    if (holdIsForced) {
      setHoldRequested(true);
      return;
    }

    setHoldRequested(userOptedInRef.current);
  }, [paymentHoldPolicy, paymentHoldAvailable, holdIsForced]);

  // Initialize Stripe Object
  const {
    data: stripe,
    isLoading: stripeLoading,
    isError: stripeFailed,
  } = useQuery({
    queryKey: ["stripe-promise"],
    queryFn: getStripe,
    enabled: paymentHoldEnabled,
  });

  // Fetch a payment intent whenever a hold is active, including an optional opt-in.
  const {
    data: paymentIntent,
    isLoading: isIntentLoading,
    isError: paymentIntentFailed,
  } = useQuery({
    queryKey: ["payment-intent", aiScoping.estimate_bucket, paymentHoldEnabled],
    queryFn: async () => {
      if (!paymentHoldEnabled) return null;
      const res = await apiRequest("POST", "/api/intake/payment-intent", {
        estimatedSize: aiScoping.estimate_bucket,
        customerId: aiScoping.matchedClientId,
      });
      return res.json();
    },
    enabled: paymentHoldEnabled,
    refetchOnWindowFocus: false,
  });

  if (pricingGate.status === "loading" || coverageGate.status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center py-16 animate-in fade-in duration-300">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <p className="text-sm text-muted-foreground">
          Loading request options...
        </p>
      </div>
    );
  }

  if (pricingGate.status === "error" || !paymentHoldPolicy) {
    return (
      <div className="py-8 animate-in fade-in duration-300 max-w-xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            We couldn't load request options, so this request can't be
            submitted yet.
          </AlertDescription>
        </Alert>
        <Button
          type="button"
          size="lg"
          className={`${intakeStackedPrimaryCtaClassName} mt-4`}
          onClick={() => void refetchPricing()}
          disabled={pricingFetching}
        >
          {pricingFetching ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Retrying...
            </>
          ) : (
            "Retry"
          )}
        </Button>
      </div>
    );
  }

  if (coverageGate.status === "error") {
    return (
      <div className="py-8 animate-in fade-in duration-300 max-w-xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            We couldn't check whether this request is covered by your plan.
            Retry before we set up a card hold.
          </AlertDescription>
        </Alert>
        <Button
          type="button"
          size="lg"
          className={`${intakeStackedPrimaryCtaClassName} mt-4`}
          onClick={() => void refetchCoverage()}
          disabled={coverageFetching}
        >
          {coverageFetching ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Retrying...
            </>
          ) : (
            "Retry"
          )}
        </Button>
      </div>
    );
  }

  const intentSkipsPayment = Boolean(paymentIntent?.noPaymentRequired);
  const holdActive = paymentHoldEnabled && !intentSkipsPayment;
  const cardReady = Boolean(
    holdActive && paymentIntent?.clientSecret && stripe,
  );
  const paymentSetupFailed = holdActive && (paymentIntentFailed || stripeFailed);
  const paymentSetupPending =
    holdActive &&
    !cardReady &&
    !paymentSetupFailed &&
    (isIntentLoading || stripeLoading || !paymentIntent || !stripe);

  return (
    <CommercialForm
      {...props}
      paymentRequired={intentSkipsPayment ? false : paymentRequired}
      paymentHoldPolicy={paymentHoldPolicy}
      paymentHoldAvailable={intentSkipsPayment ? false : paymentHoldAvailable}
      paymentHoldEnabled={intentSkipsPayment ? false : paymentHoldEnabled}
      holdRequested={intentSkipsPayment ? false : holdRequested}
      onHoldRequestedChange={handleHoldRequestedChange}
      isUrgent={isUrgent}
      onUrgentChange={setIsUrgent}
      clientSecret={cardReady ? paymentIntent?.clientSecret : undefined}
      paymentIntentId={cardReady ? paymentIntent?.paymentIntentId : undefined}
      paymentAmount={paymentIntent?.amount}
      paymentSetupPending={paymentSetupPending}
      pricing={pricing ?? null}
      stripeInstance={cardReady && stripe ? stripe : null}
      productiveCoverage={productiveCoverage}
      isCovered={isCovered}
    />
  );
}
